import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, ArrowRight, RefreshCw } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export default function AlunoCadastroConfirmacao() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as any)?.email || "";
  const [resending, setResending] = useState(false);

  const emailDomain = email.split("@")[1] || "";
  const isGmail = emailDomain.includes("gmail");
  const isOutlook = emailDomain.includes("outlook") || emailDomain.includes("hotmail") || emailDomain.includes("live");
  const isYahoo = emailDomain.includes("yahoo");

  const mailLinks = [
    ...(isGmail ? [{ label: "Abrir Gmail", url: "https://mail.google.com" }] : []),
    ...(isOutlook ? [{ label: "Abrir Outlook", url: "https://outlook.live.com" }] : []),
    ...(isYahoo ? [{ label: "Abrir Yahoo Mail", url: "https://mail.yahoo.com" }] : []),
  ];

  // Fallback if no known provider
  if (mailLinks.length === 0 && email) {
    mailLinks.push({ label: "Abrir meu e-mail", url: `https://${emailDomain}` });
  }

  const handleResend = async () => {
    if (!email) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: `${window.location.origin}/aluno/onboarding` },
      });
      if (error) throw error;
      toast({ title: "E-mail reenviado!", description: "Verifique sua caixa de entrada." });
    } catch {
      toast({ title: "Erro ao reenviar", description: "Tente novamente em alguns segundos.", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
      {/* Animated envelope */}
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", duration: 0.6 }}
        className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6"
      >
        <Mail className="w-10 h-10 text-primary" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-center max-w-sm"
      >
        <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
          Falta só um passo!
        </h1>
        <p className="text-muted-foreground mb-2">
          Enviamos um e-mail de verificação para:
        </p>

        {email && (
          <p className="font-semibold text-foreground text-lg mb-6 break-all">
            {email}
          </p>
        )}

        <div className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6 text-left space-y-3">
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
            <p className="text-sm text-foreground">Abra seu e-mail e procure a mensagem do <strong>Corre Digital</strong></p>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
            <p className="text-sm text-foreground">Clique no botão <strong>"Validar minha conta"</strong></p>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
            <p className="text-sm text-foreground">Pronto! Você volta aqui e segue preenchendo seu perfil</p>
          </div>
        </div>

        {/* Deep link to email provider */}
        {mailLinks.length > 0 && (
          <div className="space-y-2 mb-4">
            {mailLinks.map((link) => (
              <Button
                key={link.url}
                variant="hero"
                size="xl"
                className="w-full"
                onClick={() => window.open(link.url, "_blank")}
              >
                {link.label}
                <ArrowRight className="w-5 h-5" />
              </Button>
            ))}
          </div>
        )}

        {/* Spam hint */}
        <p className="text-xs text-muted-foreground mb-4">
          Não encontrou? Verifique a pasta de <strong>spam</strong> ou <strong>promoções</strong>.
        </p>

        {/* Resend */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResend}
          disabled={resending}
          className="text-muted-foreground"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${resending ? "animate-spin" : ""}`} />
          {resending ? "Reenviando..." : "Reenviar e-mail"}
        </Button>
      </motion.div>
    </div>
  );
}
